import React from "react";
import { Minus, Plus, Link2, Share2 } from "lucide-react";
import { toast } from "sonner";
import { WINE_BOTTLES, MIXED_CASE_SIZE, CASE_PRESETS, caseShareUrl, describeMix } from "../../mock";
import MixedCasePreview from "./MixedCasePreview";

const sameMix = (a, b) => WINE_BOTTLES.every((w) => (a[w.name] || 0) === (b[w.name] || 0));

const shareCase = async (mix) => {
  const url = caseShareUrl(mix);
  const text = `Our proposed Leslie West Africa tasting case — ${describeMix(mix).replace(/^Mixed tasting case · /, "")}`;
  if (navigator.share) {
    try { await navigator.share({ title: "Tasting case", text, url }); return; } catch (e) { if (e?.name === "AbortError") return; }
  }
  try { await navigator.clipboard.writeText(url); toast.success("Link copied — send it to a colleague to review this exact case."); }
  catch { window.prompt("Copy this link to share your case:", url); }
};

const tid = (s) => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/\s+/g, "-");

export default function MixedCaseBuilder({ mix, onChange }) {
  const total = Object.values(mix).reduce((s, n) => s + n, 0);
  const full = total >= MIXED_CASE_SIZE;
  const set = (name, n) => onChange({ ...mix, [name]: Math.max(0, n) });
  const tiers = [...new Set(WINE_BOTTLES.map((b) => b.tier))];

  return (
    <div className="mt-5 rounded-2xl border border-[#1f4a33]/15 bg-[#f6f1e7] p-5" data-testid="mixed-case-builder">
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-[11px] tracking-[0.18em] uppercase text-[#5e6b61]">Build your tasting case</p>
          <p className="text-sm text-[#10271a] mt-1">Pick {MIXED_CASE_SIZE} bottles across the ranges. Each case ships identically.</p>
        </div>
        <div className="text-right shrink-0">
          <p className={`font-display text-3xl leading-none ${full ? "text-[#1f4a33]" : "text-[#10271a]"}`} data-testid="mixed-case-count">{total}<span className="text-base text-[#5e6b61]"> / {MIXED_CASE_SIZE}</span></p>
          <p className="text-[10px] tracking-[0.14em] uppercase text-[#5e6b61] mt-1">{full ? "Case complete" : `${MIXED_CASE_SIZE - total} to go`}</p>
        </div>
      </div>
      <div className="mt-3 h-1.5 rounded-full bg-[#10271a]/10 overflow-hidden"><div className="h-full bg-[#1f4a33] transition-[width] duration-300" style={{ width: `${Math.min(100, (total / MIXED_CASE_SIZE) * 100)}%` }} /></div>

      <p className="mt-5 text-[10px] tracking-[0.2em] uppercase text-[#7c5230]">Start from a suggestion</p>
      <div className="mt-2 flex gap-2 overflow-x-auto pb-1 -mx-1 px-1" data-testid="case-presets">
        {CASE_PRESETS.map((p) => {
          const on = sameMix(mix, p.mix);
          return (
            <button key={p.id} type="button" onClick={() => onChange({ ...p.mix })} aria-pressed={on} data-testid={`preset-${p.id}`}
              className={`shrink-0 text-left rounded-2xl border px-4 py-3 min-w-[160px] transition-colors ${on ? "bg-[#1f4a33] border-[#1f4a33] text-[#f6f1e7]" : "bg-[#fbf8f1] border-[#10271a]/10 text-[#10271a] hover:border-[#1f4a33]/60"}`}>
              <span className="block text-[13px] font-semibold tracking-wide">{p.name}</span>
              <span className={`block text-[11px] mt-0.5 ${on ? "text-[#f6f1e7]/70" : "text-[#5e6b61]"}`}>{p.blurb}</span>
            </button>
          );
        })}
        {total > 0 && (
          <button type="button" onClick={() => onChange({})} data-testid="preset-clear" className="shrink-0 self-center text-[11px] tracking-[0.12em] uppercase font-semibold text-[#7c5230] px-3 hover:text-[#10271a]">Clear</button>
        )}
      </div>
      <p className="mt-2 text-[11px] text-[#5e6b61]">Tap a preset, then tweak the bottles below.</p>

      <MixedCasePreview mix={mix} />
      {total > 0 && (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3" data-testid="share-case-row">
          <p className="text-[11px] text-[#5e6b61]">{full ? "Happy with it? Share this exact case with colleagues before you enquire." : "You can share a half-built case too — colleagues can finish it."}</p>
          <button type="button" onClick={() => shareCase(mix)} data-testid="share-case" className="inline-flex items-center gap-2 rounded-full border border-[#1f4a33]/30 text-[#1f4a33] px-4 py-2 text-[11px] tracking-[0.12em] uppercase font-semibold hover:border-[#1f4a33] hover:bg-[#1f4a33]/5 transition-colors">
            {navigator.share ? <Share2 size={14} /> : <Link2 size={14} />} {navigator.share ? "Share this case" : "Copy link to this case"}
          </button>
        </div>
      )}
      <div className="mt-5 space-y-4">
        {tiers.map((tier) => (
          <div key={tier}>
            <p className="text-[10px] tracking-[0.2em] uppercase text-[#7c5230] mb-2">{tier} range</p>
            <ul className="divide-y divide-[#10271a]/8">
              {WINE_BOTTLES.filter((b) => b.tier === tier).map((b) => {
                const n = mix[b.name] || 0;
                return (
                  <li key={b.name} className="flex items-center justify-between py-2" data-testid={`mix-row-${tid(b.name)}`}>
                    <span className="text-[#10271a]">{b.name}</span>
                    <div className="flex items-center gap-2">
                      <button type="button" onClick={() => set(b.name, n - 1)} disabled={n === 0} aria-label={`Remove ${b.name}`} data-testid={`mix-minus-${tid(b.name)}`} className="h-8 w-8 rounded-full border border-[#10271a]/15 flex items-center justify-center text-[#10271a] disabled:opacity-30 hover:border-[#1f4a33]"><Minus size={14} /></button>
                      <span className="w-6 text-center font-display text-xl text-[#10271a] tabular-nums" data-testid={`mix-count-${tid(b.name)}`}>{n}</span>
                      <button type="button" onClick={() => set(b.name, n + 1)} disabled={full} aria-label={`Add ${b.name}`} data-testid={`mix-plus-${tid(b.name)}`} className="h-8 w-8 rounded-full bg-[#1f4a33] text-[#f6f1e7] flex items-center justify-center disabled:opacity-30 hover:bg-[#10271a]"><Plus size={14} /></button>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}
